import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { setCsrfToken, setUnauthorizedHandler } from './api/client';
import { can, qk, useMe } from './api/queries';
import { Shell } from './components/Shell';
import { Empty } from './components/ui';
import { ApprovalDetailPage, ApprovalsPage } from './pages/Approvals';
import { DashboardPage } from './pages/Dashboard';
import { DataSourcesPage } from './pages/DataSources';
import { EmergencyPage } from './pages/Emergency';
import { InvitePage } from './pages/Invite';
import { LocationsPage } from './pages/Locations';
import { LoginPage } from './pages/Login';
import { MonitoringPage } from './pages/Monitoring';
import { ReportsPage } from './pages/Reports';
import { MediaPage } from './pages/Media';
import { PlatformSettingsPage } from './pages/PlatformSettings';
import { AdminInvitePage } from './pages/platform/AdminInvite';
import { PlatformAdminsPage } from './pages/platform/PlatformAdmins';
import { PlatformTenantsPage } from './pages/PlatformTenants';
import { PlaylistEditorPage } from './pages/PlaylistEditor';
import { PlaylistsPage } from './pages/Playlists';
import { PrayerTimesPage } from './pages/prayer/PrayerTimesPage';
import { SchedulesPage } from './pages/Schedules';
import { ScheduleEditorPage } from './pages/ScheduleEditor';
import { ScreenAddPage } from './pages/ScreenAdd';
import { ScreenDetailPage } from './pages/ScreenDetail';
import { ScreensPage } from './pages/Screens';
import { SlideDesignPage } from './pages/SlideDesign';
import { StudioPage } from './pages/Studio';
import { TemplatesPage } from './pages/Templates';
import { UsersPage } from './pages/Users';
import { AccountPage } from './pages/account/Account';
import { LoginMfaPage, MfaRequiredPage } from './pages/account/LoginMfa';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/account/PasswordReset';
import { hasFeature } from './api/features';

// Vorschau mit Player-Engine nur bei Bedarf laden
const PreviewPage = lazy(() => import('./pages/Preview').then((m) => ({ default: m.PreviewPage })));

export function App() {
  const { data: me, isLoading } = useMe();
  const qc = useQueryClient();
  const loc = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setCsrfToken(me?.csrfToken ?? null);
  }, [me?.csrfToken]);
  useEffect(() => {
    setUnauthorizedHandler(() => qc.setQueryData(qk.me, null));
    return () => setUnauthorizedHandler(null);
  }, [qc]);

  if (isLoading) return <div className="content muted">{t('common.loading')}</div>;

  if (!me) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/admin-invite" element={<AdminInvitePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/password/forgot" element={<ForgotPasswordPage />} />
        <Route path="/password/reset" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc.pathname }} />} />
      </Routes>
    );
  }

  // Link „Passwort zurücksetzen“ funktioniert auch mit offener Sitzung
  if (loc.pathname === '/password/reset') return <ResetPasswordPage />;
  // M02: zweiter Faktor ausstehend; MFA-Pflicht ohne Einrichtung: erst einrichten (4.2)
  if (me.mfaPending) return <LoginMfaPage />;
  if (me.mfaSetupRequired) return <MfaRequiredPage me={me} />;

  const tenant = !!me.activeTenantId;
  const home = tenant ? '/' : me.user.isPlatformAdmin ? '/platform/tenants' : '/no-tenant';
  // M20 Vorschau: eigene Vollbild-Ansicht ohne Navigation (öffnet meist in einem neuen Tab)
  if (tenant && loc.pathname.startsWith('/preview/')) {
    return (
      <Routes>
        <Route path="/preview/:type/:id" element={<Suspense fallback={<div className="content muted">{t('common.loading')}</div>}><PreviewPage /></Suspense>} />
        <Route path="*" element={<NotFound home={home} />} />
      </Routes>
    );
  }
  return (
    <Shell>
      <Routes>
        <Route path="/login" element={<Navigate to={(loc.state as { from?: string } | null)?.from ?? home} replace />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/admin-invite" element={<AdminInvitePage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/security" element={<AccountPage />} />
        <Route path="/password/forgot" element={<Navigate to="/account" replace />} />
        {can(me, 'tenants.manage') && <Route path="/platform/tenants" element={<PlatformTenantsPage />} />}
        {can(me, 'system.settings') && <Route path="/platform/settings" element={<PlatformSettingsPage />} />}
        {can(me, 'platform.admins.manage') && <Route path="/platform/admins" element={<PlatformAdminsPage />} />}
        {tenant && <>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/screens" element={<ScreensPage />} />
          <Route path="/screens/add" element={<ScreenAddPage />} />
          <Route path="/screens/:id" element={<ScreenDetailPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/playlists" element={<PlaylistsPage />} />
          <Route path="/playlists/:id" element={<PlaylistEditorPage />} />
          <Route path="/playlists/:id/items/:itemId/design" element={<SlideDesignPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/studio/:id" element={<StudioPage />} />
          <Route path="/schedules" element={<SchedulesPage />} />
          <Route path="/schedules/:id" element={<ScheduleEditorPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          {hasFeature(me, 'emergency') && <Route path="/emergency" element={<EmergencyPage />} />}
          {hasFeature(me, 'prayer') && <Route path="/prayer-times" element={<PrayerTimesPage />} />}
          {can(me, 'users.read') && <Route path="/users" element={<UsersPage />} />}
          {can(me, 'monitoring.read') && hasFeature(me, 'monitoring') && <Route path="/monitoring" element={<MonitoringPage />} />}
          {can(me, 'reports.read') && hasFeature(me, 'reports') && <Route path="/reports" element={<ReportsPage />} />}
          {hasFeature(me, 'approval') && <Route path="/approvals" element={<ApprovalsPage />} />}
          {hasFeature(me, 'approval') && <Route path="/approvals/:id" element={<ApprovalDetailPage />} />}
          {can(me, 'datasources.read') && hasFeature(me, 'datasources') && <Route path="/datasources" element={<DataSourcesPage />} />}
        </>}
        <Route path="/no-tenant" element={<Empty icon="building" title={t('shell.noTenant')} text={t('shell.noTenantText')} />} />
        <Route path="*" element={tenant || me.user.isPlatformAdmin ? <NotFound home={home} /> : <Navigate to="/no-tenant" replace />} />
      </Routes>
    </Shell>
  );
}

function NotFound({ home }: { home: string }) {
  const { t } = useTranslation();
  const loc = useLocation();
  if (loc.pathname === '/') return <Navigate to={home} replace />;
  return <Empty icon="alert" title={t('common.notFound')} action={<a className="btn" href={home}>{t('common.back')}</a>} />;
}
